import api from '@/libs/axiosInstance';
import { devBoostSetupOrNull } from '@/libs/boostDevPreview';
import type { ApiResponse, MentionBoostSetupResponse } from '@fluenceur/types';
import {
  useQuery,
  type QueryClient,
  type Updater,
  type UseQueryResult,
} from '@tanstack/react-query';

const MENTION_BOOST_SETUP_QUERY_KEY_PREFIX = 'mentionBoostSetup';

/**
 * Prefix key, and the one to invalidate with: TanStack matches by prefix, so it
 * reaches every per-account variant of this mention's setup.
 */
export const mentionBoostSetupQueryKey = (mentionId: string | null | undefined) => [
  MENTION_BOOST_SETUP_QUERY_KEY_PREFIX,
  mentionId,
];

/**
 * Update every mounted setup variant for a mention. The setup query is also
 * keyed by the account a marketer picked, so writing only the two-part prefix
 * creates a cache entry no rendered panel reads. With no mounted variant there
 * is nothing to optimistically update; the next query reads the server state.
 */
export const updateMentionBoostSetupQueries = (
  queryClient: QueryClient,
  mentionId: string,
  updater: Updater<MentionBoostSetupResponse | undefined, MentionBoostSetupResponse | undefined>
) => {
  const queryKey = mentionBoostSetupQueryKey(mentionId);
  const matchingQueries = queryClient.getQueriesData<MentionBoostSetupResponse>({ queryKey });

  if (matchingQueries.length === 0) {
    return;
  }

  queryClient.setQueriesData<MentionBoostSetupResponse>({ queryKey }, updater);
};

/** Prefix invalidation reaches every preferred-ad-account setup variant. */
export const invalidateMentionBoostSetupQueries = (queryClient: QueryClient, mentionId: string) =>
  queryClient.invalidateQueries({ queryKey: mentionBoostSetupQueryKey(mentionId) });

const fetchMentionBoostSetup = async (
  mentionId: string,
  preferredAdAccountId?: string | null
): Promise<MentionBoostSetupResponse> => {
  // The readiness the response carries is resolved against one ad account.
  // Naming the account the marketer picked is what makes its blockers (no
  // payment method, DSA declaration missing) describe THAT account rather than
  // the brand's saved default. A blank value is a 422 server-side, so the param
  // is sent only when it actually names something.
  const preferred = preferredAdAccountId?.trim();
  const response = await api.get<ApiResponse<MentionBoostSetupResponse>>(
    `/dashboard/mentions/${mentionId}/boost-setup`,
    preferred ? { params: { preferredAdAccountId: preferred } } : undefined
  );

  if (!response.data.data) {
    throw new Error(response.data.message || 'Mention boost setup is unavailable.');
  }

  return response.data.data;
};

interface UseMentionBoostSetupQueryOptions {
  mentionId: string | null | undefined;
  enabled?: boolean;
  // The account the marketer actively picked, if any. Null leaves the server on
  // the brand's saved default.
  preferredAdAccountId?: string | null;
}

export const useMentionBoostSetupQuery = ({
  mentionId,
  enabled = true,
  preferredAdAccountId = null,
}: UseMentionBoostSetupQueryOptions): UseQueryResult<MentionBoostSetupResponse, Error> => {
  return useQuery<MentionBoostSetupResponse, Error>({
    queryKey: [...mentionBoostSetupQueryKey(mentionId), preferredAdAccountId],
    queryFn: () => {
      if (!mentionId) {
        return Promise.reject(new Error('Mention ID is required.'));
      }
      const devPreview = devBoostSetupOrNull(mentionId);
      if (devPreview) {
        return Promise.resolve(devPreview);
      }
      return fetchMentionBoostSetup(mentionId, preferredAdAccountId);
    },
    enabled: !!mentionId && enabled,
    // A new pick changes the key, so without this the whole form would fall
    // back to its skeleton on every account switch. Keeping the previous
    // response holds the form steady while the fresh readiness lands.
    placeholderData: (previous) => previous,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export { fetchMentionBoostSetup };
